import './Issue.scss';
import { Grid, useMediaQuery } from '@mui/material';
import { KeyboardEvent, useState } from 'react';
// import { useTheme } from '@mui/system';
import DetailsDialog from './DetailsDialog/DetailsDialog';
import StatusBadge from '../StatusBadge/StatusBadge';
import { useTranslation } from 'react-i18next';

interface Props {
  issue: any;
  issuesIndex: number;
  staticContent: any;
  issueTitl: any;
}

const Issue = ({ issue, issuesIndex, staticContent, issueTitl }: Props) => {
  // const theme = useTheme();
  // const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isTablet = useMediaQuery('(min-width:768px) and (max-width:900px)');
  const { t } = useTranslation();
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  const {
    affected_api,
    details,
    environment,
    ticket_id,
    error_message,
    last_updated,
    summary,
    targeted_fix_date,
    use_case,
    workaround
  } = issue;

  const formatDate = (dateString: string) => {
    if (!dateString) return 'TBC';
    const locale = t('issue.formatDate');
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));

    const formattedMonth = date.toLocaleString(locale, { month: 'long' });

    return locale === 'en-US'
      ? `${formattedMonth} ${day}, ${year}`
      : `${day} ${formattedMonth} ${year}`;
  };

  const getHeaderClassName = (index: number) =>
    index === 0 ? 'issue-header' : 'issue-header issue-header--mt';

  const getIssueClassName = () => {
    let className = 'issue';

    // Add first class to remove border for first issue
    if (issuesIndex === 0) {
      className += ' issue--first';
    }

    // Add background for odd indexed items
    if (issuesIndex % 2 !== 0) {
      className += ' background-alco-sno';
    }

    return className;
  };

  const getDisplayedStatus = () => {
    if (issue.status === staticContent.statuses.resolved && last_updated) {
      const [year, month, day] = last_updated.split('-').map(Number);
      const resolvedDate = new Date(Date.UTC(year, month - 1, day));
      const now = new Date();
      const diffDays = Math.floor(
        (now.getTime() - resolvedDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (diffDays > 90) {
        return staticContent.statuses.archived;
      }
    }
    
    return issue.status;
  };

  const handleOnClickViewDetails = () => {
    setShowDetailsDialog(true);
  };

  const handleOnKeyDownCloseViewDetails = (
    event: KeyboardEvent<SVGSVGElement>
  ) => {
    if (event.key === 'Enter') {
      setShowDetailsDialog(false);
    }
  };

  const handleOnKeyDownViewDetails = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      setShowDetailsDialog(true);
    }
  };

  const col1Fields = {
    affected_api,
    summary
  };

  const col2Fields = {
    environment,
    ticket_id
  };

  const col3Fields = {
    last_updated: formatDate(last_updated),
    targeted_fix_date: formatDate(targeted_fix_date)
  };

  const detailFields = {
    ...col1Fields,
    details,
    use_case,
    error_message,
    workaround,
    ...col2Fields,
    ...col3Fields
  };

  return (
    <>
      <Grid
        className={getIssueClassName()}
        container
        mt={issuesIndex === 0 ? 5 : 0}
        spacing={2}
      >
        <Grid
          item
          display={'flex'}
          flexDirection={'column'}
          justifyContent="space-between"
          md={2}
          sm={isTablet ? 4 : 4}
          xs={12}
          sx={{ height: '100%' }}
        >
          <h2 className="issue-title">{issueTitl}</h2>
          <StatusBadge
            status={getDisplayedStatus()}
            statuses={staticContent.statuses}
            parentComponentName="Known Issues"
          />
        </Grid>
        {isTablet ? (
          <Grid item xs={12} sm={8} md={10}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <>
                  {Object.entries(col1Fields).map(([key, value], index) => {
                    let displayValue = key === 'summary' ? details : value;
                    if (
                      key === 'summary' &&
                      typeof details === 'string' &&
                      details.length > 250
                    ) {
                      displayValue = details.substring(0, 250) + '...';
                    }
                    return (
                      <Grid item xs={12} key={key}>
                        <div className={getHeaderClassName(index)}>
                          {staticContent.fields[key]}
                        </div>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: Array.isArray(value)
                              ? displayValue
                                  .map((item) => item.value)
                                  .join(', ')
                              : displayValue
                          }}
                        />
                      </Grid>
                    );
                  })}
                </>
                <div
                  style={{ display: 'flex', flexDirection: 'row', gap: '20px' }}
                >
                  {Object.entries(col2Fields).map(([key, value], index) => (
                    <Grid item xs={12} key={key}>
                      <div
                        className={getHeaderClassName(
                          index + Object.keys(col1Fields).length
                        )}
                      >
                        {staticContent.fields[key]}
                      </div>
                      <div dangerouslySetInnerHTML={{ __html: value }} />
                    </Grid>
                  ))}
                </div>
                <div
                  style={{ display: 'flex', flexDirection: 'row', gap: '20px' }}
                >
                  {Object.entries(col3Fields).map(([key, value], index) => (
                    <Grid item xs={12} key={key}>
                      <div
                        className={getHeaderClassName(
                          index +
                            Object.keys(col1Fields).length +
                            Object.keys(col2Fields).length
                        )}
                      >
                        {staticContent.fields[key]}
                      </div>
                      <div dangerouslySetInnerHTML={{ __html: value }} />
                    </Grid>
                  ))}
                </div>
                <div
                  className="issue-header--link"
                  onClick={() => handleOnClickViewDetails()}
                  onKeyDown={(e) => handleOnKeyDownViewDetails(e)}
                  tabIndex={0}
                  role="button"
                  aria-label={`${staticContent.viewDetails}${issueTitl ? ' for ' + issueTitl : ''}`}
                >
                  {staticContent.viewDetails}
                </div>
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <>
            <Grid item xs={12} sm={8} md={5}>
              {Object.entries(col1Fields).map(([key, value], index) => {
                let displayValue = key === 'summary' ? details : value;
                if (
                  key === 'summary' &&
                  typeof details === 'string' &&
                  details.length >= 242
                ) {
                  displayValue = details.substring(0, 242) + '...';
                }
                return (
                  <Grid item xs={12} key={key}>
                    <div className={getHeaderClassName(index)}>
                      {staticContent.fields[key]}
                    </div>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: Array.isArray(value)
                          ? displayValue
                              .map((item) => item.value)
                              .join(', ')
                          : displayValue
                      }}
                    />
                  </Grid>
                );
              })}
              <div
                className="issue-header--link"
                onClick={() => handleOnClickViewDetails()}
                onKeyDown={(e) => handleOnKeyDownViewDetails(e)}
                tabIndex={0}
                role="button"
                aria-label={`${staticContent.viewDetails}${issueTitl ? ' for ' + issueTitl : ''}`}
              >
                {staticContent.viewDetails}
              </div>
            </Grid>
            <Grid
              item
              xs={12}
              sm={8}
              md={2.5}
              sx={{ display: isTablet ? 'flex' : 'auto' }}
            >
              {Object.entries(col2Fields).map(([key, value], index) => {
                return (
                  <Grid item xs={12} key={key}>
                    <div className={getHeaderClassName(index)}>
                      {staticContent.fields[key]}
                    </div>
                    <div dangerouslySetInnerHTML={{ __html: value }} />
                  </Grid>
                );
              })}
            </Grid>
            <Grid
              item
              xs={12}
              sm={8}
              md={2.5}
              sx={{ display: isTablet ? 'flex' : 'auto' }}
            >
              {Object.entries(col3Fields).map(([key, value], index) => {
                return (
                  <Grid item xs={12} key={key}>
                    <div className={getHeaderClassName(index)}>
                      {staticContent.fields[key]}
                    </div>
                    <div dangerouslySetInnerHTML={{ __html: value }} />
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
      </Grid>
      <DetailsDialog
        issueId={issue.ticket_id}
        fields={detailFields}
        open={showDetailsDialog}
        title={issueTitl}
        staticContent={staticContent}
        status={getDisplayedStatus()}
        handleOnClose={() => setShowDetailsDialog(false)}
        handleOnKeyDown={(e) => handleOnKeyDownCloseViewDetails(e)}
      />
    </>
  );
};

export default Issue;
